import axios from 'axios';
import qs from 'query-string';
import { getItem, setItem, KEYS } from '@app/cache';
import { authStore } from '@stores';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const AUTH_TOKEN = getItem(KEYS.AUTH_TOKEN);
if (AUTH_TOKEN) {
  API.defaults.headers.common.Authorization = `Bearer ${AUTH_TOKEN}`;
}

API.interceptors.response.use(
  response => response,
  error => {
    if (error.response.status === 401) {
      authStore.logout();
    }
    return Promise.reject(error.response.data);
  },
);

const cacheAuthenticationHeaders = headers => {
  setItem(KEYS.AUTH_TOKEN, headers['access-token']);
  setItem(KEYS.CLIENT, headers.client);
  setItem(KEYS.EXPIRY, headers.expiry);
  setItem(KEYS.UID, headers.uid);
  setItem(KEYS.TOKEN_TYPE, headers['token-type']);
};

const setAuthenticationHeaders = () => {
  API.defaults.headers.common['access-token'] = getItem(KEYS.AUTH_TOKEN);
  API.defaults.headers.common.client = getItem(KEYS.CLIENT);
  API.defaults.headers.common.expiry = getItem(KEYS.EXPIRY);
  API.defaults.headers.common.uid = getItem(KEYS.UID);
  API.defaults.headers.common['token-type'] = getItem(KEYS.TOKEN_TYPE);
};

const apiPrefix = 'v1';

const APIRoutes = {
  SIGN_IN: `/${apiPrefix}/auth/sign_in`, // POST
  CUSTOMER: `/${apiPrefix}/customer`, // POST
  SIGN_OUT: `/${apiPrefix}/auth/sign_out`, // DELETE
  ALBUMS: (page = 1, per = 24, phrase = '') =>
    `/delivery_partners/albums?page=${page}&per=${per}&q=${phrase}`,
  ALBUM: (uuid, stems = false) =>
    `/delivery_partners/albums/${uuid}?stems=${stems}`,
  DOWNLOAD_ALBUM_ZIP: uuid =>
    `${process.env.REACT_APP_API_URL}/delivery_partners/albums/${uuid}/download_zip`,
  DOWNLOAD_TRACKS_ZIP: (tracks, kind, json) =>
    qs.stringifyUrl({
      url: `${process.env.REACT_APP_API_URL}/delivery_partners/tracks/download_zip`,
      query: {
        'ids[]': tracks,
        kind,
        json_only: json,
      },
    }),
  TRACKS: (page = 1, per = 24, phrase = '') =>
    `/delivery_partners/tracks?page=${page}&per=${per}&q=${phrase}`,
  DOWNLOAD_TRACK: (id, kind) =>
    `${process.env.REACT_APP_API_URL}/delivery_partners/tracks/${id}/download?kind=${kind}`,
  LABELS: '/delivery_partners/labels',
  ALBUMS_BY_LABEL: (
    uuid,
    stems = false,
    page = 1,
    per = 24,
    phrase = '',
    order = '',
  ) =>
    `/delivery_partners/labels/${uuid}/albums?stems=${stems}&page=${page}&per=${per}&q=${phrase}&order=${order}`,
  DOWNLOAD_META_BATCH: (albums, kind) => {
    return qs.stringifyUrl({
      url: `${process.env.REACT_APP_API_URL}/delivery_partners/albums/multiple_download_xls`,
      query: {
        'uuid[]': albums,
        kind,
      },
    });
  },
  DOWNLOAD_MUSIC_BATCH: (albums, kind, json) => {
    if (kind === 'stems') {
      return qs.stringifyUrl({
        url: `${process.env.REACT_APP_API_URL}/delivery_partners/albums/multiple_download_stems`,
        query: {
          'uuid[]': albums,
          u: authStore.userUID,
          json_only: json,
        },
      });
    }

    return qs.stringifyUrl({
      url: `${process.env.REACT_APP_API_URL}/delivery_partners/albums/multiple_download_zip`,
      query: {
        'uuid[]': albums,
        kind,
        u: authStore.userUID,
        json_only: json,
      },
    });
  },
  DOWNLOAD_CATALOGUE: uuid =>
    `${process.env.REACT_APP_API_URL}/delivery_partners/labels/${uuid}/albums/download_pdf`,
  TRACKS_HISTORY: (page, per) =>
    `/delivery_partners/tracks/history?page=${page}&per=${per}`,
};

export { API, APIRoutes, cacheAuthenticationHeaders, setAuthenticationHeaders };
